/* eslint-disable react-hooks/exhaustive-deps */
import React, {Dispatch, useContext, useEffect, useState} from 'react';
import Pagination from "rc-pagination";
import {dateFormatOptions} from "../../../common/dateFormatterConfig";
import {locale} from "../../../common/table/paginatorConfig";
import {IFuelInvoice, IFuelInvoiceSortFields, DocumentType} from "../../../models/fuel-invoice";
import {SortDirection} from "../../../common/interfaces/IPageResponse";
import {SortIcons} from "../../SortIcons/SortIcons";
import { Button, OverlayTrigger, Spinner, Tooltip } from 'react-bootstrap';
import { AuthenticationContext } from 'services/context/authentication.context';
import DocumentService from 'services/document.service';
import { getPriceWithoutVAT } from 'helpers';
import PDFPreviewModal from 'components/Modals/PDFPreviewModal/PDFPreviewModal';
import printJS from 'print-js';
import { IFilePreview } from 'common/interfaces/IFilePreview';
import moment from 'moment';

export function FuelInvoicesTable(props: any) {
  const [sortedByField, setSortedByField]: [IFuelInvoiceSortFields, Dispatch<any>] = useState(IFuelInvoiceSortFields.DOCUMENT_DATE_CREATED);
  const [sortDirection, setSortDirection]: [SortDirection, Dispatch<any>] = useState(SortDirection.DESC);
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const [filePreview, setFilePreview] = useState<IFilePreview<string> | null>(null);
  const [filePreviewLoading, setFilePreviewLoading] = useState<number>();
  const context = useContext(AuthenticationContext);

  const getInvoiceFile = async () => {
    if (context.state.customerId === null || isDownloading || !filePreview)
      return;
    setIsDownloading(true);

    try{
      await DocumentService.getDocumentFileWithDownload(filePreview.id);
    }finally{
      setIsDownloading(false)
    }      
  }

  const getInvoicePreview = async (document: IFuelInvoice) => {
    if (context.state.customerId === null || filePreviewLoading)
      return;

      setFilePreviewLoading(document.remoteDocumentId);

    try{
      const res = await DocumentService.getDocumentFile(document.remoteDocumentId);   

      setFilePreview({
        id: document.remoteDocumentId,
        fileName: document.documentNumber,
        objectUrl: res.data,
        payload: document.printForm
      });

    }finally{
      setFilePreviewLoading(undefined)
    }
  }

  useEffect(() => {
    props.onSortChange(sortedByField, sortDirection);
  }, [sortedByField, sortDirection]);

  const invoices: Array<IFuelInvoice> = props.invoices.content;
  const pageResponse = props.invoices;

  const getDocumentTypeLabel = (type: DocumentType) => type === DocumentType.VA_1 ? 'Koondarve' : 'Hulgiarve';

  const invoicesTableRows = invoices.map((invoice: IFuelInvoice) =>
    <tr key={'row-' + invoice.remoteDocumentId}>
      <td>
        <div className="font-weight-bold">
          <span className="d-md-inline d-none">{invoice.documentNumber}</span>
          <span className="d-md-none d-inline">{invoice.documentNumber.split("-", 2)[0]}</span>
        </div>
      </td>
      <td className="d-sm-table-cell d-none">
        {getDocumentTypeLabel(invoice.documentType)}
      </td>
      <td>
        {invoice.documentDateCreated ? new Intl.DateTimeFormat('et', dateFormatOptions).format(new Date(invoice.documentDateCreated)) : ""}
      </td>
      <td>
        {invoice.paymentDeadlineDate ? new Intl.DateTimeFormat('et', dateFormatOptions).format(new Date(invoice.paymentDeadlineDate)) : ""}
      </td>
      <td>
        {invoice.totalAmount}
      </td>
      <td>
        {getPriceWithoutVAT(invoice.totalAmount, moment(invoice.documentDateCreated), 2)}
      </td>
      <td>
        {invoice.paid
          ? <div className='status status--paid'>
            Arve tasutud
          </div>
          : <div className='status status--unpaid'>
            Tasumata
          </div>}
      </td>
            <td>
                <div className="row__actions">
                  <OverlayTrigger 
                    delay={250}
                    overlay={
                      <Tooltip id={`tooltip-fuel-invoice-${invoice.remoteDocumentId}`}>
                          <p style={{margin: '3px'}}>Vaata</p>
                      </Tooltip>
                    } 
                    placement={"auto"}
                  >
                    <Button 
                      size="sm" 
                      variant="secondary" 
                      className="d-flex align-items-center btn-outline-icon" 
                      onClick={() => getInvoicePreview(invoice)}
                      >
                          {
                            filePreviewLoading === invoice.remoteDocumentId ? (
                              <Spinner size="sm" animation="border"/>
                            ) : (
                              <div className='icon__preview-file action-icon'></div>
                            )
                          }
                    </Button>
                  </OverlayTrigger>
                </div>
            </td>
    </tr>
  );

  const handleSortClick = (currentSortColumn: IFuelInvoiceSortFields) => {
    if (sortedByField.match(currentSortColumn)) {
      setSortDirection(sortDirection === SortDirection.DESC ? SortDirection.ASC : SortDirection.DESC);
    } else {
      setSortDirection(SortDirection.ASC);
      setSortedByField(currentSortColumn);
    }
  };

  return (
    <>
      <div className="table__container scroll-icon-md">
        <table className='invoices__table table table-responsive-md table-hover'>
          <thead>
          <tr>
            <th className="sort-down-up-hover">
              <div className="d-flex align-items-center"
                  onClick={() => handleSortClick(IFuelInvoiceSortFields.DOCUMENT_NUMBER)}>
                <span>Arve</span>
                <SortIcons
                  sortedByField={sortedByField}
                  sortDirection={sortDirection}
                  currentSortColumn={IFuelInvoiceSortFields.DOCUMENT_NUMBER}/>
              </div>
            </th>
            <th className="d-sm-table-cell d-none sort-down-up-hover">
              <div className="d-flex align-items-center" onClick={() => handleSortClick(IFuelInvoiceSortFields.DOCUMENT_TYPE)}>
                <span>Tüüp</span>
                <SortIcons
                  sortedByField={sortedByField}
                  sortDirection={sortDirection}
                  currentSortColumn={IFuelInvoiceSortFields.DOCUMENT_TYPE}/>
              </div>
            </th>
            <th className="sort-down-up-hover">
              <div className="d-flex align-items-center" onClick={() => handleSortClick(IFuelInvoiceSortFields.DOCUMENT_DATE_CREATED)}>
                <span>Kuupäev</span>
                <SortIcons
                  sortedByField={sortedByField}
                  sortDirection={sortDirection}
                  currentSortColumn={IFuelInvoiceSortFields.DOCUMENT_DATE_CREATED}/>
              </div>
            </th>
            <th className="sort-down-up-hover">
              <div className="d-flex align-items-center" onClick={() => handleSortClick(IFuelInvoiceSortFields.PAYMENT_DEADLINE_DATE)}>
                <span>Maksetähtaeg</span>
                <SortIcons
                  sortedByField={sortedByField}
                  sortDirection={sortDirection}
                  currentSortColumn={IFuelInvoiceSortFields.PAYMENT_DEADLINE_DATE}/>
              </div>
            </th>
            <th className="sort-down-up-hover">
              <div className="d-flex align-items-center" onClick={() => handleSortClick(IFuelInvoiceSortFields.TOTAL_AMOUNT)}>
                <span>Summa</span>
                <SortIcons
                  sortedByField={sortedByField}
                  sortDirection={sortDirection}
                  currentSortColumn={IFuelInvoiceSortFields.TOTAL_AMOUNT}/>
              </div>
            </th>
            <th>Summa KM-ta</th>
            <th className="sort-down-up-hover">
              <div className="d-flex align-items-center" onClick={() => handleSortClick(IFuelInvoiceSortFields.IS_PAID)}>
                <span>Staatus</span>
                <SortIcons
                  sortedByField={sortedByField}
                  sortDirection={sortDirection}
                  currentSortColumn={IFuelInvoiceSortFields.IS_PAID}/>
              </div>
            </th>
            <th/>
          </tr>
          </thead>
          <tbody>
            {props.isRequesting ?
              <tr>
                <td colSpan={8} className="spinner-cell">
                  <div className="p-4 w-100 max-w-100vw d-flex align-items-center justify-content-center">
                    <Spinner animation="grow" />
                  </div>
                </td>
              </tr> :
              invoicesTableRows
            }
          </tbody>
          <tfoot>
          {pageResponse ? <tr>
            <td colSpan={8}>
              <div className="pagination-wrapper">
                <Pagination
                  locale={locale}
                  current={pageResponse.number + 1}
                  total={pageResponse.totalElements}
                  pageSize={pageResponse.size}
                  prefixCls={"pagination"}
                  onChange={props.onPageChange}
                  showSizeChanger={false}
                  hideOnSinglePage={true}
                />
              </div>
            </td>
          </tr> : null}
          </tfoot>
        </table>
      </div>
      <PDFPreviewModal
          show={!!filePreview}
          onHide={() => setFilePreview(null)}
          onDownload={() => getInvoiceFile()}
          isDownloading={isDownloading}
          onPrint={() => printJS({printable: filePreview?.objectUrl, type: 'pdf'})}
          pdfPreview={filePreview?.objectUrl}
      />
    </>
  )
}